import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { api, timesheetApi } from '../services/api'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import toast from 'react-hot-toast'

export default function ManageTimesheets() {
    const [loading, setLoading] = useState(true)
    const [timesheets, setTimesheets] = useState([])
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [typeFilter, setTypeFilter] = useState('all')
    const [returningToDraft, setReturningToDraft] = useState(null) // uuid of timesheet being returned

    useEffect(() => {
        loadTimesheets()
    }, [])

    async function loadTimesheets() {
        setLoading(true)
        try {
            // Get submitted and exported timesheets (status 1 and 2)
            const response = await api.get('/api/timesheets')
            if (response.success) {
                // Filter to only show submitted (1) and exported (2)
                const filtered = (response.timesheets || []).filter(
                    ts => ts.status === '1' || ts.status === '2'
                )
                setTimesheets(filtered)
            } else {
                toast.error('Failed to load timesheets')
            }
        } catch (error) {
            console.error('Error:', error)
            toast.error('Error loading timesheets')
        }
        setLoading(false)
    }

    async function handleReturnToDraft(e, ts) {
        e.preventDefault() // Prevent Link navigation
        e.stopPropagation()

        if (!confirm(`Return ${ts.referenceNo || 'this timesheet'} to draft? It will need to be resubmitted after editing.`)) return

        setReturningToDraft(ts.uuid)
        try {
            const response = await timesheetApi.returnToDraft(ts.uuid)
            if (response.success) {
                toast.success(`${ts.referenceNo || 'Timesheet'} returned to draft`)
                // Remove from list
                setTimesheets(prev => prev.filter(t => t.uuid !== ts.uuid))
            } else {
                toast.error(response.message || 'Failed to return to draft')
            }
        } catch (error) {
            toast.error('Error returning to draft')
        }
        setReturningToDraft(null)
    }

    const filteredTimesheets = timesheets.filter(ts => {
        // Search filter
        const searchMatch =
            (ts.workOrderNumber || '').toLowerCase().includes(search.toLowerCase()) ||
            (ts.customer || '').toLowerCase().includes(search.toLowerCase()) ||
            (ts.referenceNo || '').toLowerCase().includes(search.toLowerCase()) ||
            (ts.employeeName || '').toLowerCase().includes(search.toLowerCase())

        // Status filter
        const statusMatch = statusFilter === 'all' ||
            (statusFilter === 'submitted' && ts.status === '1') ||
            (statusFilter === 'exported' && ts.status === '2')

        // Type filter
        const typeMatch = typeFilter === 'all' || ts.tsType === typeFilter

        return searchMatch && statusMatch && typeMatch
    })

    const getTypeLabel = (type) => {
        switch (type) {
            case '0': return 'Timesheet'
            case '1': return 'Repair in Shop'
            case '2': return 'Packing Slip'
            case '3': return 'Internal'
            case '4': return 'Pickup Slip'
            case '5': return 'Vendor Slip'
            default: return 'Timesheet'
        }
    }

    const getTypeColor = (type) => {
        switch (type) {
            case '0': return 'bg-blue-100 text-blue-700'
            case '1': return 'bg-purple-100 text-purple-700'
            case '2': return 'bg-green-100 text-green-700'
            case '3': return 'bg-gray-100 text-gray-700'
            case '4': return 'bg-orange-100 text-orange-700'
            default: return 'bg-blue-100 text-blue-700'
        }
    }

    const getStatusLabel = (status) => {
        switch (status) {
            case '2': return 'Exported'
            case '1': return 'Submitted'
            default: return 'Draft'
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case '2': return 'bg-blue-100 text-blue-700'
            case '1': return 'bg-green-100 text-green-700'
            default: return 'bg-yellow-100 text-yellow-700'
        }
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return ''
        try {
            const date = new Date(dateStr)
            return date.toLocaleDateString()
        } catch {
            return dateStr
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }

    return (
        <div>
            {/* Header */}
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Manage Timesheets</h1>
                    <p className="text-blue-100 mt-1">{filteredTimesheets.length} timesheets</p>
                </div>
                <Button onClick={loadTimesheets}>🔄 Refresh</Button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                        <Input
                            placeholder="Search by job number, customer, reference, or employee..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rdi-blue focus:border-transparent"
                        >
                            <option value="all">All Status</option>
                            <option value="submitted">Submitted</option>
                            <option value="exported">Exported</option>
                        </select>
                    </div>

                    <div>
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rdi-blue focus:border-transparent"
                        >
                            <option value="all">All Types</option>
                            <option value="0">Timesheet</option>
                            <option value="1">Repair in Shop</option>
                            <option value="2">Packing Slip</option>
                            <option value="3">Internal</option>
                            <option value="4">Pickup Slip</option>
                            <option value="5">Vendor Slip</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-md p-4 text-center">
                    <p className="text-2xl font-bold text-gray-800">{timesheets.length}</p>
                    <p className="text-sm text-gray-500">Total</p>
                </div>
                <div className="bg-white rounded-xl shadow-md p-4 text-center">
                    <p className="text-2xl font-bold text-green-500">
                        {timesheets.filter(t => t.status === '1').length}
                    </p>
                    <p className="text-sm text-gray-500">Submitted</p>
                </div>
                <div className="bg-white rounded-xl shadow-md p-4 text-center">
                    <p className="text-2xl font-bold text-blue-500">
                        {timesheets.filter(t => t.status === '2').length}
                    </p>
                    <p className="text-sm text-gray-500">Exported</p>
                </div>
                <div className="bg-white rounded-xl shadow-md p-4 text-center">
                    <p className="text-2xl font-bold text-purple-500">
                        {filteredTimesheets.length}
                    </p>
                    <p className="text-sm text-gray-500">Filtered</p>
                </div>
            </div>

            {/* List */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                {filteredTimesheets.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <p className="text-4xl mb-2">📋</p>
                        <p>No timesheets found</p>
                        <p className="text-sm mt-2">Try adjusting your filters</p>
                    </div>
                ) : (
                    <>
                    {/* Phones: one card per timesheet. No horizontal scrolling. */}
                    <ul className="md:hidden divide-y">
                        {filteredTimesheets.map((ts) => (
                            <li key={ts.uuid || ts.id} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="font-semibold text-gray-900 break-words">
                                            {ts.workOrderNumber ? `${ts.workOrderNumber}` : 'No Job #'}
                                        </p>
                                        <p className="text-sm text-gray-600 break-words">{ts.customer || '-'}</p>
                                    </div>
                                    <span className="shrink-0 font-mono text-sm text-gray-600">{ts.referenceNo || '-'}</span>
                                </div>

                                <p className="mt-1 text-sm text-gray-600 break-words">
                                    {ts.employeeName || '-'}
                                    {ts.month && ` · ${formatDate(ts.month)}`}
                                </p>

                                <div className="mt-2 flex flex-wrap gap-2">
                                    <span className={`text-xs px-2 py-1 rounded ${getTypeColor(ts.tsType)}`}>
                                        {getTypeLabel(ts.tsType)}
                                    </span>
                                    <span className={`text-xs px-2 py-1 rounded ${getStatusColor(ts.status)}`}>
                                        {getStatusLabel(ts.status)}
                                    </span>
                                </div>

                                <div className="mt-3 flex items-center gap-2">
                                    <Link
                                        to={`/timesheet/${ts.uuid}`}
                                        state={{ from: 'manage' }}
                                        className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg bg-blue-50 text-rdi-blue font-medium text-sm active:bg-blue-100"
                                    >
                                        View →
                                    </Link>
                                    {ts.status === '1' && (
                                        <button
                                            onClick={(e) => handleReturnToDraft(e, ts)}
                                            disabled={returningToDraft === ts.uuid}
                                            className="flex-1 min-h-[44px] rounded-lg bg-orange-50 text-orange-700 font-medium text-sm active:bg-orange-100 disabled:opacity-50"
                                        >
                                            {returningToDraft === ts.uuid ? 'Returning...' : 'Return to Draft'}
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>

                    {/* Tablet and up: the table stays. */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="text-left p-4 font-semibold text-gray-600">Reference</th>
                                    <th className="text-left p-4 font-semibold text-gray-600">Job / Customer</th>
                                    <th className="text-left p-4 font-semibold text-gray-600">Employee</th>
                                    <th className="text-left p-4 font-semibold text-gray-600">Date</th>
                                    <th className="text-left p-4 font-semibold text-gray-600">Type</th>
                                    <th className="text-left p-4 font-semibold text-gray-600">Status</th>
                                    <th className="text-left p-4 font-semibold text-gray-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {filteredTimesheets.map((ts) => (
                                    <tr key={ts.uuid || ts.id} className="hover:bg-gray-50">
                                        <td className="p-4">
                                            <span className="font-mono text-sm">{ts.referenceNo || '-'}</span>
                                        </td>
                                        <td className="p-4">
                                            <p className="font-medium text-gray-800">
                                                {ts.workOrderNumber ? `${ts.workOrderNumber}` : 'No Job #'}
                                            </p>
                                            <p className="text-sm text-gray-500">{ts.customer || '-'}</p>
                                        </td>
                                        <td className="p-4 text-gray-600">
                                            {ts.employeeName || '-'}
                                        </td>
                                        <td className="p-4 text-gray-600">
                                            {formatDate(ts.month) || '-'}
                                        </td>
                                        <td className="p-4">
                                            <span className={`text-xs px-2 py-1 rounded ${getTypeColor(ts.tsType)}`}>
                                                {getTypeLabel(ts.tsType)}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <span className={`text-xs px-2 py-1 rounded ${getStatusColor(ts.status)}`}>
                                                {getStatusLabel(ts.status)}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <Link
                                                    to={`/timesheet/${ts.uuid}`}
                                                    state={{ from: 'manage' }}
                                                    className="text-rdi-blue hover:text-rdi-blue-dark font-medium text-sm"
                                                >
                                                    View →
                                                </Link>
                                                {ts.status === '1' && (
                                                    <button
                                                        onClick={(e) => handleReturnToDraft(e, ts)}
                                                        disabled={returningToDraft === ts.uuid}
                                                        className="text-orange-600 hover:text-orange-800 font-medium text-sm ml-2 disabled:opacity-50"
                                                    >
                                                        {returningToDraft === ts.uuid ? 'Returning...' : 'Return to Draft'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    </>
                )}
            </div>
        </div>
    )
}